import merge from 'deepmerge'
import { type Session } from 'next-auth'

import { CookieRegistry } from '@/constants/cookieRegistry'
import { ALL_CANADA_LABEL_EN, ALL_CANADA_LOCATION_ID } from '@/constants/location'
import locationTree from '@/constants/location/locationTree.json'
import { type ApiLocale } from '@/domain/locale'
import { type UserLocation } from '@/generated/graphql-types'
import { PLATFORM_VERSION } from '@/lib/ga/constants/ga'
import { OptimizelyClient } from '@/lib/optimizely/OptimizelyClient'
import { getCookieByKey } from '@/utils/cookies/getCookieByKey'
import { getHashedUserIdFromCookie } from '@/utils/cookies/getHashedUserIdFromCookie'

import { type SearchPageDataLayer } from '../hooks/useSrpTracking'

type LocationItemDataLayer = { id: number; n: string }
export type LocationDataLayer = {
  /** Current Location */
  c: LocationItemDataLayer
  /** L0 Location Path */
  l0: { id: number; n: string }
  /** L1 location Path */
  l1?: LocationItemDataLayer
  /** L2 location Path */
  l2?: LocationItemDataLayer
  /** L2 location Path */
  l3?: LocationItemDataLayer
}

const getLocationPath = (id: number, locale: ApiLocale): LocationItemDataLayer[] => {
  const paths: LocationItemDataLayer[] = []
  const location = locationTree.find((item) => item.id === id)

  if (!location || id === 0) return paths

  const currLocation = { id: location.id, n: location.name[locale] }

  paths.push(currLocation)
  return paths.concat(getLocationPath(location.parentId, locale))
}

/**
 * Returns base dataLayer fields that are present on all pages. Everything within
 * this file should be page agnostic (no page logic should exist within here).
 * Refer to https://wiki.es.ecg.tools/pages/viewpage.action?spaceKey=ANYL&title=Data+Layer for more info.
 */
export async function buildDataLayer({
  additionalData = {},
  apiLocale,
  location,
  session,
}: {
  additionalData?: Record<string, unknown>
  apiLocale: ApiLocale
  location: UserLocation
  session: Session | null
}) {
  const getDevice = () => {
    return {
      ua: window?.navigator.userAgent,
      ck: getCookieByKey(document.cookie, CookieRegistry.MACHINE_ID),
    }
  }

  const getUser = async () => {
    const isLoggedIn = !!session?.user
    const accountType = session?.user?.type
    const categoryData: Partial<SearchPageDataLayer> = additionalData
    const categoryId = categoryData?.c?.c?.id
    const sessionTestGroups = await OptimizelyClient.getGaSessionTestGroups(categoryId)

    return {
      li: isLoggedIn,
      tg: { stg: sessionTestGroups },
      ...(isLoggedIn
        ? { huid: getHashedUserIdFromCookie(), ...(accountType && { at: accountType }) }
        : {}),
    }
  }

  const getLocation = () => {
    const l: LocationDataLayer = {
      c: { id: location.id, n: `${location.name[apiLocale]}` },
      l0: { id: ALL_CANADA_LOCATION_ID, n: ALL_CANADA_LABEL_EN },
    }

    /** LOCATION PATH*/
    const paths = getLocationPath(location.id, apiLocale).reverse()

    const l1Location = paths[0]
    const l2Location = paths[1]
    const l3Location = paths[2]

    if (l1Location) l.l1 = { id: l1Location.id, n: `${l1Location.n}` }
    if (l2Location) l.l2 = { id: l2Location.id, n: `${l2Location.n}` }
    if (l3Location) l.l3 = { id: l3Location.id, n: `${l3Location.n}` }

    return l
  }

  const getPlatformVersion = () => {
    return {
      pl: PLATFORM_VERSION,
    }
  }

  const baseDataLayer = {
    l: getLocation(),
    u: await getUser(),
    d: getDevice(),
    p: getPlatformVersion(),
  }

  return merge(baseDataLayer, additionalData)
}

export const clearDataLayer = () => {
  pushToDataLayer(function () {
    /**
     * TODO: find a way to type "this"
     */
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.reset()
  })
}

export const pushToDataLayer = (data: Record<string, unknown> | (() => void)) => {
  if (typeof window === 'undefined') return

  // data can be nested e.g. pushToDataLayer({ 's.srt.f': sortBy, })
  const pushData = () => window?.dataLayer?.push(data)

  /**
   * requestIdleCallback is not available in all browsers
   * but we can use setTimeout as a fallback
   * https://caniuse.com/?search=requestidlecallback
   */
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(() => {
      pushData()
    })
  } else {
    /**
     * setTimeout with 0ms delay adds the callback to the event loop
     * which unblocks the main thread and allows the browser to render
     */
    setTimeout(() => {
      pushData()
    }, 0)
  }
}
